import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { WorkspaceService } from '@core/services/auth/workspace.service';
import { environment } from 'src/environments/environment';

/** One `usageLimits.status[dim]` entry as written by the backend evaluator. */
interface UsageLimitStatusEntry {
  tier: 'warn' | 'blocked';
  pct: number;
  mode: 'block' | 'warn';
  /** ISO — the banner ignores entries past this (window rolled over). */
  expiresAt: string;
  updatedAt?: string;
}

interface BannerView {
  severity: 'warn' | 'blocked';
  dimensions: string[];
}

/**
 * Pick the most severe currently-active status. Pure — exported for tests.
 * Ignores entries past their `expiresAt` (the window rolled over) and entries
 * without an active tier. Blocked wins over warn.
 */
export function selectActiveBannerView(
  status: Record<string, UsageLimitStatusEntry>,
  nowMs: number
): BannerView | null {
  const active = Object.entries(status).filter(
    ([, e]) =>
      e &&
      (e.tier === 'warn' || e.tier === 'blocked') &&
      (!e.expiresAt || new Date(e.expiresAt).getTime() > nowMs)
  );
  if (active.length === 0) return null;

  const blocked = active.filter(([, e]) => e.tier === 'blocked');
  const chosen = blocked.length > 0 ? blocked : active;
  return {
    severity: blocked.length > 0 ? 'blocked' : 'warn',
    dimensions: chosen.map(([dim]) => dim)
  };
}

/**
 * Stable id for "this exact situation" — order-independent on `dimensions`, so
 * a status map that re-serializes in a different key order does not read as a
 * new situation. Pure — exported for tests. Used to persist a dismissal that
 * survives reload but reopens on a materially different problem (see class
 * docs on `UsageLimitBannerComponent`).
 */
export function bannerSignature(v: BannerView): string {
  return `${v.severity}:${[...v.dimensions].sort().join(',')}`;
}

/**
 * Short inline hint of the affected dimensions. The fully localized names live
 * in `admin.usageLimits.dim.*` (admin gauge); here a compact slug is enough.
 * Pure — exported for tests.
 */
export function formatDimensionLabels(dims: string[]): string {
  return dims
    .map((d) => d.replace(/^(ai|outbound)\./, '').replace(/\./g, ' '))
    .join(', ');
}

/**
 * User-side usage-limit banner.
 *
 * Reads the current workspace's `usageLimits.status` map (stamped server-side
 * by the threshold-alert evaluator and by manual superadmin blocks) in real
 * time and surfaces a single banner: amber when approaching a limit (≥80%),
 * red when a limit was reached/blocked.
 *
 * Renders nothing when there is no active status — so it is safe to mount once
 * at the top of the authenticated shell.
 *
 * ## Dismissal survives reload, per situation
 *
 * A plain in-memory "dismissed" flag reset on every reload and every fresh
 * mount of the shell — closing it bought silence for a few seconds, then it
 * was back, on every screen, because the shell remounts on a full navigation.
 * Reported as noise on 2026-09-02.
 *
 * The fix is not "hide forever": a `pct`/`mode` update the backend evaluator
 * writes for a genuinely NEW problem (a different dimension trips, or warn
 * escalates to blocked) must still surface — silencing that unconditionally
 * would hide a real block indefinitely, which is worse than the annoyance
 * being fixed. So the dismissal is keyed to a SIGNATURE of the active view
 * (severity + which dimensions) and persisted in `localStorage`, scoped to the
 * workspace: the same known situation stays hidden across reloads and remounts,
 * but a materially different one reopens the banner.
 */
@Component({
  selector: 'app-usage-limit-banner',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (view(); as v) {
      @if (!dismissed()) {
        <div
          class="ul-banner"
          [class.ul-blocked]="v.severity === 'blocked' && !unmetered"
          [class.ul-unmetered]="unmetered">
          <mat-icon class="ul-icon">
            {{ unmetered ? 'science' : v.severity === 'blocked' ? 'block' : 'warning' }}
          </mat-icon>
          <div class="ul-text">
            <div class="ul-title">
              {{
                (unmetered
                  ? 'shared.usageLimitBanner.unmeteredTitle'
                  : v.severity === 'blocked'
                    ? 'shared.usageLimitBanner.blockedTitle'
                    : 'shared.usageLimitBanner.warnTitle'
                ) | translate
              }}
            </div>
            <div class="ul-body">
              {{
                (unmetered
                  ? 'shared.usageLimitBanner.unmeteredBody'
                  : v.severity === 'blocked'
                    ? 'shared.usageLimitBanner.blockedBody'
                    : 'shared.usageLimitBanner.warnBody'
                ) | translate
              }}
            </div>
            <div class="ul-body">
              {{ 'shared.usageLimitBanner.affected' | translate }}:
              <span class="ul-dims">{{ dimensionLabels(v.dimensions) }}</span>
            </div>
          </div>
          <button
            mat-icon-button
            class="ul-dismiss"
            (click)="dismiss()"
            [attr.aria-label]="'shared.usageLimitBanner.dismiss' | translate">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }
    }
  `,
  styles: [
    `
      .ul-banner {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 16px;
        margin: 12px 16px 0;
        border-radius: 8px;
        background: #fef3c7;
        color: #92400e;
        border: 1px solid #fcd34d;
      }
      .ul-banner.ul-blocked {
        background: #fee2e2;
        color: #991b1b;
        border-color: #fca5a5;
      }
      /* Hors production : rien ne bloque, donc rien ne doit alarmer. L indigo
         se lit comme une note de developpement, pas comme un incident. */
      .ul-banner.ul-unmetered {
        background: #e0e7ff;
        color: #3730a3;
        border-color: #a5b4fc;
      }
      .ul-icon {
        flex: 0 0 auto;
        margin-top: 2px;
      }
      .ul-text {
        flex: 1 1 auto;
        min-width: 0;
      }
      .ul-title {
        font-weight: 600;
      }
      .ul-body {
        font-size: 0.875rem;
        opacity: 0.95;
      }
      .ul-dims {
        font-weight: 600;
      }
      .ul-dismiss {
        flex: 0 0 auto;
      }
    `
  ]
})
export class UsageLimitBannerComponent {
  private workspaceService = inject(WorkspaceService);
  private firestore = inject(Firestore);

  /**
   * En local et sur dev, aucun quota n est applique cote serveur.
   *
   * Une bannière rouge y serait doublement fausse : elle annonce un blocage qui
   * n a pas eu lieu, et elle apprend a ignorer une couleur qui, en production,
   * signale un vrai refus. La variante indigo dit ce qui est vrai — ici on ne
   * compte pas — et garde le rouge pour ce qu il designe.
   */
  protected readonly unmetered = !environment.production;

  /** Signature of the view the user last dismissed, or null — see class docs. */
  private readonly dismissedSignature = signal<string | null>(null);

  private readonly status = toSignal(this.watchStatus(), {
    initialValue: {} as Record<string, UsageLimitStatusEntry>
  });

  protected readonly view = computed<BannerView | null>(() =>
    selectActiveBannerView(this.status() ?? {}, Date.now())
  );

  protected readonly dismissed = computed(() => {
    const v = this.view();
    return v !== null && this.dismissedSignature() === bannerSignature(v);
  });

  constructor() {
    // Re-read the stored dismissal whenever the workspace resolves/changes —
    // it is unknown at construction time (async-loaded), and a superadmin
    // switching workspaces must not carry one workspace's dismissal to another.
    effect(() => {
      const workspaceId = this.workspaceService.currentWorkspaceIdSignal();
      this.dismissedSignature.set(
        workspaceId ? this.readStoredSignature(workspaceId) : null
      );
    });
  }

  protected dismiss(): void {
    const v = this.view();
    const workspaceId = this.workspaceService.currentWorkspaceIdSignal();
    if (!v || !workspaceId) return;
    const signature = bannerSignature(v);
    this.dismissedSignature.set(signature);
    this.writeStoredSignature(workspaceId, signature);
  }

  protected dimensionLabels(dims: string[]): string {
    return formatDimensionLabels(dims);
  }

  private storageKey(workspaceId: string): string {
    return `proporio.usageLimitBanner.dismissed.${workspaceId}`;
  }

  /**
   * Best-effort — a private-browsing tab or a full storage quota must never
   * break the banner, just fall back to "nothing dismissed yet".
   */
  private readStoredSignature(workspaceId: string): string | null {
    try {
      return localStorage.getItem(this.storageKey(workspaceId));
    } catch {
      return null;
    }
  }

  private writeStoredSignature(workspaceId: string, signature: string): void {
    try {
      localStorage.setItem(this.storageKey(workspaceId), signature);
    } catch {
      // Degrades to "reappears next reload" — never breaks the click itself.
    }
  }

  /**
   * Live `usageLimits.status` for the current workspace. Called from a field
   * initializer, so `toObservable` runs inside the component injection context.
   */
  private watchStatus(): Observable<Record<string, UsageLimitStatusEntry>> {
    return toObservable(this.workspaceService.currentWorkspaceIdSignal).pipe(
      switchMap((id) =>
        id
          ? (
              docData(doc(this.firestore, 'workspaces', id)) as Observable<{
                usageLimits?: {
                  status?: Record<string, UsageLimitStatusEntry>;
                };
              }>
            ).pipe(map((d) => d?.usageLimits?.status ?? {}))
          : of({} as Record<string, UsageLimitStatusEntry>)
      )
    );
  }
}
